<template>
  <div class="dev-routes-page">
    <div class="header">
      <div class="header-top">
        <h1>开发路由导航</h1>
        <button
          title="管理全局参数"
          class="global-params-btn"
          @click="openGlobalParamsModal"
        >
          全局参数
        </button>
      </div>
      <div class="header-tips">
        <p>点击"参数"按钮管理路由参数 · 点击"全局参数"管理所有路由的通用参数 · 优先级 : 路由参数 > 全局参数</p>
      </div>
      <div class="server-info">
        <div class="info-item">
          <span class="label">本机访问:</span>
          <a
            :href="localUrl"
            target="_blank"
            class="url"
          >{{ localUrl }}</a>
          <button
            class="copy-btn"
            @click="copyUrl(localUrl)"
          >
            复制
          </button>
        </div>
        <div class="info-item">
          <span class="label">网络访问:</span>
          <a
            :href="networkUrl"
            target="_blank"
            class="url"
          >{{ networkUrl }}</a>
          <button
            class="copy-btn"
            @click="copyUrl(networkUrl)"
          >
            复制
          </button>
        </div>
      </div>
    </div>

    <div class="routes-section">
      <div class="route-category">
        <div class="routes-grid">
          <div
            v-for="(route, index) in commonRoutesList"
            :key="`common-${index}`"
            class="route-card"
          >
            <div class="route-header">
              <span class="route-number">{{ index + 1 }}</span>
              <h3 class="route-name">
                {{ route.name }}
              </h3>
              <div class="route-actions">
                <button
                  class="copy-btn"
                  @click="copyUrl(getFullUrl(route.path, route.currentParams))"
                >
                  复制
                </button>
                <button
                  class="qr-btn"
                  @click="showQRCode(route)"
                >
                  二维码
                </button>
                <button
                  title="管理参数"
                  class="params-btn"
                  @click="openParamsModal(route)"
                >
                  参数
                </button>
              </div>
            </div>
            <div class="route-url">
              <a
                :href="getFullUrl(route.path, route.currentParams)"
                target="_blank"
                class="url-link"
              >
                {{ getFullUrl(route.path, route.currentParams) }}
              </a>
            </div>

            <!-- 路由参数预览 -->
            <div
              v-if="route.currentParams && Object.keys(route.currentParams).length > 0"
              class="params-preview"
            >
              <div class="params-label">
                路由参数:
              </div>
              <div class="params-tags">
                <span
                  v-for="(value, key) in route.currentParams"
                  :key="key"
                  class="param-tag route"
                >
                  {{ key }}={{ value }}
                </span>
              </div>
            </div>

            <!-- 全局参数预览 -->
            <div
              v-if="Object.keys(globalParams).length > 0"
              class="global-params-preview"
            >
              <div class="params-label">
                全局参数:
              </div>
              <div class="params-tags">
                <span
                  v-for="(value, key) in globalParams"
                  :key="key"
                  class="param-tag global"
                >
                  {{ key }}={{ value }}
                </span>
              </div>
            </div>

            <!-- 预设展示区域 -->
            <div
              v-if="getRoutePresets(route.path).length > 0"
              class="presets-preview"
            >
              <div class="presets-label">
                预设配置:
              </div>
              <div class="presets-list">
                <button
                  v-for="preset in getRoutePresets(route.path)"
                  :key="preset.name"
                  class="preset-quick-btn"
                  :class="{ active: isPresetActive(route, preset) }"
                  :title="`加载预设: ${preset.name}`"
                  @click="loadPresetQuick(route, preset)"
                >
                  {{ preset.name }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 二维码显示区域 -->
      <div
        v-if="showQRModal"
        class="qr-modal-overlay"
        @click="closeQRCode"
      >
        <div
          class="qr-modal"
          @click.stop
        >
          <div class="qr-content">
            <div class="qr-header">
              <h4>二维码</h4>
              <button
                class="qr-close-btn"
                @click="closeQRCode"
              >
                关闭
              </button>
            </div>
            <div class="qr-code-container">
              <canvas
                ref="qrCanvas"
                class="qr-canvas"
              />
            </div>
            <p class="qr-url">
              {{ currentQRUrl }}
            </p>
          </div>
        </div>
      </div>

      <!-- 参数管理弹窗 -->
      <div
        v-if="showParamsModal"
        class="modal-overlay"
        @click="closeParamsModal"
      >
        <div
          class="modal-content"
          @click.stop
        >
          <div class="modal-header">
            <h3>参数管理 - {{ currentRoute.name }}</h3>
            <button
              class="close-btn"
              @click="closeParamsModal"
            >
              取消
            </button>
          </div>
          <div class="modal-body">
            <div class="params-section">
              <div class="section-header">
                <h4>当前参数</h4>
                <button
                  class="add-param-btn"
                  @click="addNewParam"
                >
                  + 添加参数
                </button>
              </div>
              <div class="params-list">
                <div
                  v-for="(value, key) in editingParams"
                  :key="key"
                  class="param-item"
                >
                  <input
                    v-model="paramKeys[key]"
                    placeholder="参数名"
                    class="param-key-input"
                    @blur="validateParamKey(key)"
                  >
                  <span class="equals">=</span>
                  <input
                    v-model="editingParams[key]"
                    placeholder="参数值"
                    class="param-value-input"
                  >
                  <button
                    class="remove-param-btn"
                    @click="removeParam(key)"
                  >
                    删除
                  </button>
                </div>
              </div>
            </div>

            <div class="presets-section">
              <div class="section-header">
                <h4>参数预设</h4>
                <button
                  class="save-preset-btn"
                  @click="saveAsPreset"
                >
                  保存当前为预设
                </button>
              </div>
              <div class="presets-modal-list">
                <div
                  v-for="(preset, index) in routePresets"
                  :key="preset.name"
                  class="preset-item"
                >
                  <div class="preset-info">
                    <span class="preset-name">{{ preset.name }}</span>
                    <div class="preset-params">
                      <span
                        v-for="(value, key) in preset.params"
                        :key="key"
                        class="preset-param"
                      >
                        {{ key }}={{ value }}
                      </span>
                    </div>
                  </div>
                  <div class="preset-actions">
                    <button
                      class="preset-btn"
                      @click="loadPreset(preset)"
                    >
                      修改
                    </button>
                    <button
                      class="preset-btn delete"
                      @click="deletePreset(index)"
                    >
                      删除
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              class="cancel-btn"
              @click="closeParamsModal"
            >
              取消
            </button>
            <button
              class="save-btn"
              @click="saveParams"
            >
              保存
            </button>
          </div>
        </div>
      </div>

      <!-- 全局参数管理弹窗 -->
      <div
        v-if="showGlobalParamsModal"
        class="modal-overlay"
        @click="closeGlobalParamsModal"
      >
        <div
          class="modal-content"
          @click.stop
        >
          <div class="modal-header">
            <h3>全局参数管理</h3>
            <button
              class="close-btn"
              @click="closeGlobalParamsModal"
            >
              取消
            </button>
          </div>
          <div class="modal-body">
            <div class="global-params-section">
              <div class="section-header">
                <h4>全局参数</h4>
                <button
                  class="add-param-btn"
                  @click="addNewGlobalParam"
                >
                  + 添加全局参数
                </button>
              </div>
              <div class="params-list">
                <div
                  v-for="(value, key) in editingGlobalParams"
                  :key="key"
                  class="param-item"
                >
                  <input
                    v-model="globalParamKeys[key]"
                    placeholder="参数名"
                    class="param-key-input"
                    @blur="validateGlobalParamKey(key)"
                  >
                  <span class="equals">=</span>
                  <input
                    v-model="editingGlobalParams[key]"
                    placeholder="参数值"
                    class="param-value-input"
                  >
                  <button
                    class="remove-param-btn"
                    @click="removeGlobalParam(key)"
                  >
                    删除
                  </button>
                </div>
              </div>
              <div class="global-params-info">
                <p class="info-text">
                  全局参数自动应用到所有路由。优先级：路由参数 > 全局参数
                </p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              class="cancel-btn"
              @click="closeGlobalParamsModal"
            >
              取消
            </button>
            <button
              class="save-btn"
              @click="saveGlobalParams"
            >
              保存
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DevRoutes',
  data() {
    return {
      baseUrl: '',
      localUrl: '',
      networkUrl: '',
      showParamsModal: false,
      currentRoute: null,
      editingParams: {},
      paramKeys: {},
      routePresets: [],
      globalParams: {},
      showGlobalParamsModal: false,
      editingGlobalParams: {},
      globalParamKeys: {},
      showQRModal: false,
      currentQRUrl: '',
      routes: [
        { name: '新版超级会员', path: '/#/vip/v3', currentParams: {} },
        { name: '老版超级会员已开卡页', path: '/#/vip/v2', currentParams: {} },
        { name: '老版超级会员未开卡页', path: '/#/vip/v2/open', currentParams: {} },
        { name: '福利中心', path: '/#/bounty', currentParams: {} },
        { name: '商品详情', path: '/#/bounty/detail/7359411425820609003017286254', currentParams: {} },
        { name: '兑换中心', path: '/#/bounty/record', currentParams: {} },
        { name: '我的奖励金', path: '/#/bounty/exchange', currentParams: {} },
        { name: 'T专项', path: '/#/newClient', currentParams: {} },
      ],
      selectedPresetByRoute: {},
    };
  },
  computed: {
    // 常用路由（现在所有路由都是常用路由）
    commonRoutesList() {
      return this.routes;
    },
  },
  mounted() {
    this.initUrls();
    this.loadRouteParams();
    this.loadPresets();
    this.loadSelectedPresetMap();
    this.loadGlobalParams();
  },
  methods: {
    initUrls() {
      // 获取当前页面的基础URL
      const { protocol, hostname } = window.location;
      const port = window.location.port || '1024';

      // 本机地址
      this.localUrl = `${protocol}//localhost:${port}/`;

      // 页面层优先使用由服务端脚本注入的局域网IP（如果存在）
      const injectedLanIp = process && process.env && process.env.VUE_APP_LAN_IP ? process.env.VUE_APP_LAN_IP : '';
      const finalHost = injectedLanIp || hostname;

      // 网络地址：优先使用 172/192/10 等局域网网段的 IP
      this.networkUrl = `${protocol}//${finalHost}:${port}/`;
      this.baseUrl = this.networkUrl;
    },
    getFullUrl(path, params = {}) {
      // 使用网络地址，确保格式正确
      const baseNetworkUrl = this.networkUrl.endsWith('/') ? this.networkUrl.slice(0, -1) : this.networkUrl;
      const cleanPath = path.startsWith('/') ? path.substring(1) : path;
      const url = `${baseNetworkUrl}/${cleanPath}`;

      // 合并全局参数和路由参数，路由参数优先级更高
      const mergedParams = { ...this.globalParams, ...params };

      if (Object.keys(mergedParams).length === 0) {
        return url;
      }
      const queryString = new URLSearchParams(mergedParams).toString();
      return `${url}?${queryString}`;
    },
    async copyUrl(url) {
      try {
        await navigator.clipboard.writeText(url);
        if (this.$toast) {
          this.$toast('链接已复制到剪贴板');
        }
      } catch (err) {
        // 降级方案
        const textArea = document.createElement('textarea');
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        if (this.$toast) {
          this.$toast('链接已复制到剪贴板');
        }
      }
    },
    // 参数管理相关方法
    openParamsModal(route) {
      this.currentRoute = route;
      this.editingParams = { ...route.currentParams };
      this.paramKeys = {};
      // 为每个参数创建对应的key
      Object.keys(this.editingParams).forEach((key) => {
        this.paramKeys[key] = key;
      });
      this.showParamsModal = true;
    },
    closeParamsModal() {
      this.showParamsModal = false;
      this.currentRoute = null;
      this.editingParams = {};
      this.paramKeys = {};
    },
    addNewParam() {
      const newKey = `param_${Date.now()}`;
      this.$set(this.editingParams, newKey, '');
      this.$set(this.paramKeys, newKey, '');
    },
    removeParam(key) {
      this.$delete(this.editingParams, key);
      this.$delete(this.paramKeys, key);
    },
    // 失去焦点时校验参数名
    validateParamKey(key) {
      const newKey = this.paramKeys[key];
      if (!newKey || newKey.trim() === '' || newKey === key) return;

      // 检查新key是否已存在（只检查其他参数的实际值）
      const existingParamNames = Object.keys(this.editingParams);
      const otherParamNames = existingParamNames.filter((k) => k !== key);

      // 检查是否有其他参数使用了相同的参数名
      let hasDuplicate = false;
      otherParamNames.forEach((otherKey) => {
        if (this.paramKeys[otherKey] === newKey) {
          hasDuplicate = true;
        }
      });

      if (hasDuplicate) {
        this.showParamError('参数名已存在，请使用其他名称');
        this.paramKeys[key] = ''; // 置空
        return;
      }

      // 更新参数名，保持参数项顺序不变
      const value = this.editingParams[key];
      this.$set(this.editingParams, newKey, value);
      this.$delete(this.editingParams, key);
      this.$set(this.paramKeys, newKey, newKey);
      this.$delete(this.paramKeys, key);
    },
    // 显示参数错误提示
    showParamError(message) {
      // 创建临时错误提示
      const errorDiv = document.createElement('div');
      errorDiv.textContent = message;
      errorDiv.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ff4757;
        color: white;
        padding: 10px 15px;
        border-radius: 6px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(255, 71, 87, 0.3);
      `;
      document.body.appendChild(errorDiv);

      // 3秒后自动移除
      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.parentNode.removeChild(errorDiv);
        }
      }, 3000);
    },
    saveParams() {
      // 清理并标准化参数（保留空字符串和'0'等有效值）
      const cleanParams = {};
      Object.keys(this.editingParams).forEach((key) => {
        const paramKey = this.paramKeys[key];
        const value = this.editingParams[key];
        if (paramKey && String(paramKey).trim() !== '') {
          cleanParams[paramKey] = (value === undefined || value === null) ? '' : value;
        }
      });

      // 更新路由参数
      this.$set(this.currentRoute, 'currentParams', cleanParams);

      // 如果该路由当前选中了某个预设，同步更新该预设的参数
      const routeKey = this.currentRoute.path;
      const selectedName = this.selectedPresetByRoute[routeKey];
      if (selectedName) {
        const presets = this.getRoutePresets(routeKey);
        const targetPreset = presets.find((p) => p.name === selectedName);
        if (targetPreset) {
          targetPreset.params = { ...cleanParams };
          this.savePresets();
        }
      }

      // 保存到localStorage
      this.saveRouteParams();

      this.closeParamsModal();
    },
    // localStorage相关方法
    saveRouteParams() {
      const routeParams = {};
      this.routes.forEach((route) => {
        if (Object.keys(route.currentParams).length > 0) {
          routeParams[route.path] = route.currentParams;
        }
      });
      localStorage.setItem('dev-routes-params', JSON.stringify(routeParams));
    },
    loadRouteParams() {
      try {
        const saved = localStorage.getItem('dev-routes-params');
        if (saved) {
          const routeParams = JSON.parse(saved);
          this.routes.forEach((route) => {
            if (routeParams[route.path]) {
              this.$set(route, 'currentParams', routeParams[route.path]);
            }
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('加载保存的参数失败:', err);
      }
    },
    // 预设管理
    saveAsPreset() {
      // eslint-disable-next-line no-alert
      const presetName = prompt('请输入预设名称:');
      if (!presetName) return;

      // 校验重名（同一路由下不允许重名）
      const routePath = this.currentRoute.path;
      const existed = this.getRoutePresets(routePath).some((p) => p.name === presetName);
      if (existed) {
        // eslint-disable-next-line no-alert
        alert('该路由下已存在同名预设，请更换名称');
        return;
      }

      const cleanParams = {};
      Object.keys(this.editingParams).forEach((key) => {
        const paramKey = this.paramKeys[key];
        const value = this.editingParams[key];
        if (paramKey && value) {
          cleanParams[paramKey] = value;
        }
      });

      if (Object.keys(cleanParams).length === 0) {
        // eslint-disable-next-line no-alert
        alert('请先添加一些参数');
        return;
      }

      const preset = {
        name: presetName,
        routePath: this.currentRoute.path,
        params: cleanParams,
      };

      this.routePresets.push(preset);
      this.savePresets();

      // 新建预设后，清空该路由的选中预设标记，避免后续保存时误更新旧预设
      const routeKey = this.currentRoute.path;
      this.$delete(this.selectedPresetByRoute, routeKey);
      this.saveSelectedPresetMap();
    },
    loadPreset(preset) {
      if (preset.routePath !== this.currentRoute.path) {
        // eslint-disable-next-line no-alert
        alert('此预设不适用于当前路由');
        return;
      }

      this.editingParams = { ...preset.params };
      this.paramKeys = {};
      Object.keys(this.editingParams).forEach((key) => {
        this.paramKeys[key] = key;
      });
    },
    deletePreset(index) {
      // eslint-disable-next-line no-alert, no-restricted-globals
      if (confirm('确定要删除这个预设吗？')) {
        this.routePresets.splice(index, 1);
        this.savePresets();
      }
    },
    savePresets() {
      localStorage.setItem('dev-routes-presets', JSON.stringify(this.routePresets));
    },
    loadPresets() {
      try {
        const saved = localStorage.getItem('dev-routes-presets');
        if (saved) {
          this.routePresets = JSON.parse(saved);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('加载预设失败:', err);
      }
    },
    // 全局参数管理
    loadGlobalParams() {
      try {
        const saved = localStorage.getItem('dev-routes-global-params');
        if (saved) {
          this.globalParams = JSON.parse(saved);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('加载全局参数失败:', err);
      }
    },
    saveGlobalParamsToStorage() {
      localStorage.setItem('dev-routes-global-params', JSON.stringify(this.globalParams));
    },
    // 二维码相关方法
    showQRCode(route) {
      this.currentQRUrl = this.getFullUrl(route.path, route.currentParams);
      this.showQRModal = true;
      this.$nextTick(() => {
        this.generateQRCode(this.currentQRUrl);
      });
    },
    closeQRCode() {
      this.showQRModal = false;
      this.currentQRUrl = '';
    },
    async generateQRCode(url) {
      // 使用qrcode库生成真实的二维码
      const canvas = this.$refs.qrCanvas;
      if (!canvas) return;

      try {
        // 动态导入qrcode库
        const QRCode = await import('qrcode');

        // 生成二维码到canvas
        await QRCode.toCanvas(canvas, url, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('生成二维码失败:', error);
        // 如果生成失败，显示错误信息
        const ctx = canvas.getContext('2d');
        canvas.width = 200;
        canvas.height = 200;
        ctx.fillStyle = '#f0f0f0';
        ctx.fillRect(0, 0, 200, 200);
        ctx.fillStyle = '#666';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('二维码生成失败', 100, 100);
      }
    },
    // 全局参数管理弹窗
    openGlobalParamsModal() {
      this.editingGlobalParams = { ...this.globalParams };
      this.globalParamKeys = {};
      // 为每个全局参数创建对应的key
      Object.keys(this.editingGlobalParams).forEach((key) => {
        this.globalParamKeys[key] = key;
      });
      this.showGlobalParamsModal = true;
    },
    closeGlobalParamsModal() {
      this.showGlobalParamsModal = false;
      this.editingGlobalParams = {};
      this.globalParamKeys = {};
    },
    addNewGlobalParam() {
      const newKey = `global_param_${Date.now()}`;
      this.$set(this.editingGlobalParams, newKey, '');
      this.$set(this.globalParamKeys, newKey, ''); // 初始化为空字符串
    },
    removeGlobalParam(key) {
      this.$delete(this.editingGlobalParams, key);
      this.$delete(this.globalParamKeys, key);
    },
    validateGlobalParamKey(key) {
      const newKey = this.globalParamKeys[key];
      if (!newKey || newKey.trim() === '' || newKey === key) return;

      // 检查新key是否已存在（只检查其他参数的实际值）
      const existingParamNames = Object.keys(this.editingGlobalParams);
      const otherParamNames = existingParamNames.filter((k) => k !== key);

      // 检查是否有其他参数使用了相同的参数名
      let hasDuplicate = false;
      otherParamNames.forEach((otherKey) => {
        if (this.globalParamKeys[otherKey] === newKey) {
          hasDuplicate = true;
        }
      });

      if (hasDuplicate) {
        this.showParamError('参数名已存在，请使用其他名称');
        this.globalParamKeys[key] = ''; // 置空
        return;
      }

      // 更新参数名
      const value = this.editingGlobalParams[key];
      this.$set(this.editingGlobalParams, newKey, value);
      this.$delete(this.editingGlobalParams, key);
      this.$set(this.globalParamKeys, newKey, newKey);
      this.$delete(this.globalParamKeys, key);
    },
    saveGlobalParams() {
      // 清理空的参数
      const cleanParams = {};
      Object.keys(this.editingGlobalParams).forEach((key) => {
        const paramKey = this.globalParamKeys[key];
        const value = this.editingGlobalParams[key];
        if (paramKey && value) {
          cleanParams[paramKey] = value;
        }
      });

      // 更新全局参数
      this.globalParams = cleanParams;

      // 保存到localStorage
      this.saveGlobalParamsToStorage();

      this.closeGlobalParamsModal();
    },
    // 获取指定路由的预设
    getRoutePresets(routePath) {
      // 标准化路径匹配，支持带#和不带#的路径
      const normalizedPath = routePath.replace(/^#/, '');
      const presets = this.routePresets.filter((preset) => {
        const presetPath = preset.routePath.replace(/^#/, '');
        return presetPath === normalizedPath || presetPath === routePath;
      });
      return presets;
    },
    // 检查预设是否与当前参数匹配
    isPresetActive(route, preset) {
      const routeKey = route.path;
      const selectedName = this.selectedPresetByRoute[routeKey];
      return selectedName ? selectedName === preset.name : false;
    },
    // 快速加载预设
    loadPresetQuick(route, preset) {
      this.$set(route, 'currentParams', { ...preset.params });
      this.saveRouteParams();
      // 记录用户明确选择的预设名称（每路由下唯一）
      const routeKey = route.path;
      this.$set(this.selectedPresetByRoute, routeKey, preset.name);
      this.saveSelectedPresetMap();
    },
    saveSelectedPresetMap() {
      try {
        localStorage.setItem(
          'dev-routes-selected-preset',
          JSON.stringify(this.selectedPresetByRoute),
        );
      } catch (e) {
        /* no-op */
      }
    },
    loadSelectedPresetMap() {
      try {
        const saved = localStorage.getItem('dev-routes-selected-preset');
        if (saved) {
          this.selectedPresetByRoute = JSON.parse(saved);
        }
      } catch (e) {
        /* no-op */
      }
    },
  },
};
</script>

<style lang="less" scoped>
.dev-routes-page {
  min-height: 100vh;
  background: #fafafa;
  font-family:
    PingFangSC-Regular,
    Helvetica,
    STHeiti STXihei,
    Microsoft YaHei,
    Microsoft JhengHei,
    Arial,
    sans-serif;
}

.header {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 24px 0;
  margin-bottom: 32px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.02em;
  }
}

.header-top {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.header-tips {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  margin-bottom: 20px;

  p {
    margin: 0;
    font-size: 13px;
    color: #6b7280;
    line-height: 1.6;
  }
}

.global-params-btn {
  background: #3b82f6;
  border: none;
  color: white;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #374151;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
}

.server-info {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f9fafb;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.label {
  font-weight: 600;
  font-size: 13px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  min-width: 70px;
}

.url {
  color: #3b82f6;
  text-decoration: none;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 13px;
  background: white;
  padding: 8px 12px;
  border-radius: 6px;
  flex: 1;
  word-break: break-all;
  border: 1px solid #e5e7eb;
  transition: all 0.15s ease;
}

.url:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.copy-btn {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &:active {
    background: #f3f4f6;
  }
}

.qr-btn {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &:active {
    background: #f3f4f6;
  }
}

.routes-section {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px 48px;
}

.routes-section h2 {
  color: #111827;
  margin-bottom: 24px;
  font-size: 20px;
  font-weight: 600;
}

.route-category {
  margin-bottom: 48px;
}

.category-title {
  color: #111827;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.routes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
}

.route-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }
}

.route-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.route-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.route-number {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
  align-self: self-start;
}

.route-name {
  margin: 0;
  color: #111827;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.route-url {
  margin-bottom: 16px;
}

.url-link {
  color: #6b7280;
  text-decoration: none;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 12px;
  word-break: break-all;
  display: block;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  transition: all 0.15s ease;
  line-height: 1.5;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
    color: #3b82f6;
  }
}

@media (max-width: 768px) {
  .dev-routes-page {
    padding: 15px;
  }

  .routes-grid {
    grid-template-columns: 1fr;
  }

  .server-info {
    flex-direction: column;
  }

  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .route-actions {
    flex-direction: column;
  }
}

/* 参数管理相关样式 */
.params-btn {
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: #2563eb;
  }

  &:active {
    background: #1d4ed8;
  }
}

.params-preview {
  margin: 12px 0;
  padding: 12px;
  background: #eff6ff;
  border-radius: 8px;
  border: 1px solid #dbeafe;
}

.params-label {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.params-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.param-tag {
  background: #3b82f6;
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  font-weight: 500;

  &.global {
    background: #ef4444;
  }

  &.route {
    background: #10b981;
  }
}

/* 全局参数预览样式 */
.global-params-preview {
  margin: 12px 0;
  padding: 12px;
  background: #fef2f2;
  border-radius: 8px;
  border: 1px solid #fecaca;
}

.global-params-info {
  margin-top: 16px;
  padding: 12px;
  background: #f0f9ff;
  border-radius: 8px;
  border: 1px solid #e0f2fe;
}

.info-text {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
}

/* 预设展示区域样式 */
.presets-preview {
  margin: 12px 0;
  padding: 12px;
  background: #f0fdf4;
  border-radius: 8px;
  border: 1px solid #d1fae5;
}

.presets-label {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.presets-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.preset-quick-btn {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);

    &:hover {
      background: #2563eb;
      border-color: #2563eb;
    }
  }
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
  animation: fadeIn 0.15s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 85vh;
  overflow: hidden;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  animation: slideUp 0.2s ease;
  border: 1px solid #e5e7eb;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: #fafafa;

  h3 {
    margin: 0;
    color: #111827;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
}

.close-btn {
  background: white;
  border: 1px solid #d1d5db;
  font-size: 13px;
  cursor: pointer;
  color: #6b7280;
  padding: 6px 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.15s ease;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #374151;
  }

  &:active {
    background: #f3f4f6;
  }
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h4 {
  margin: 0;
  color: #111827;
  font-size: 15px;
  font-weight: 600;
}

.add-param-btn,
.save-preset-btn {
  background: #111827;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.15s ease;

  &:hover {
    background: #374151;
  }

  &:active {
    background: #4b5563;
  }
}

.params-list {
  margin-bottom: 32px;
}

.param-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 8px 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  transition: all 0.15s ease;

  &:hover {
    border-color: #d1d5db;
  }
}

.param-key-input,
.param-value-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 13px;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  transition: all 0.15s ease;
  background: #fafafa;

  &:focus {
    outline: none;
    border-color: #9ca3af;
    background: white;
  }
}

.equals {
  color: #9ca3af;
  font-weight: 600;
  font-size: 14px;
  user-select: none;
}

.remove-param-btn {
  background: white;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    background: #fef2f2;
    border-color: #fecaca;
    color: #dc2626;
  }

  &:active {
    background: #fee2e2;
  }
}

.presets-section {
  border-top: 1px solid #e5e7eb;
  padding-top: 24px;
  margin-top: 8px;
}

.presets-modal-list {
  max-height: 240px;
  overflow-y: auto;
}

.preset-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: white;
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid #e5e7eb;
  transition: all 0.15s ease;

  &:hover {
    border-color: #d1d5db;
  }
}

.preset-info {
  flex: 1;
  min-width: 0;
}

.preset-name {
  font-weight: 500;
  color: #111827;
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
}

.preset-params {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.preset-param {
  background: #f3f4f6;
  color: #6b7280;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  font-weight: 400;
  border: 1px solid #e5e7eb;
}

.preset-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 12px;
}

.preset-btn {
  padding: 4px 10px;
  border: 1px solid #e5e7eb;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  transition: all 0.15s ease;
  white-space: nowrap;
  color: #6b7280;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #374151;
  }

  &.delete {
    &:hover {
      background: #fef2f2;
      border-color: #fecaca;
      color: #dc2626;
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: white;
}

.cancel-btn,
.save-btn {
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.15s ease;
}

.cancel-btn {
  background: white;
  border: 1px solid #d1d5db;
  color: #6b7280;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #374151;
  }

  &:active {
    background: #f3f4f6;
  }
}

.save-btn {
  background: #111827;
  color: white;
  border: none;

  &:hover {
    background: #374151;
  }

  &:active {
    background: #4b5563;
  }
}

@media (max-width: 768px) {
  .header-top,
  .server-info,
  .routes-section {
    padding: 0 16px;
  }

  .server-info {
    grid-template-columns: 1fr;
  }

  .routes-grid {
    grid-template-columns: 1fr;
  }

  .modal-content {
    margin: 16px;
    max-height: 90vh;
    border-radius: 12px;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 16px;
  }

  .param-item {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .equals {
    display: none;
  }

  .preset-item {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .preset-actions {
    justify-content: stretch;
    margin-left: 0;
  }

  .preset-btn {
    flex: 1;
  }
}

/* 二维码显示样式 */
.qr-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
  animation: fadeIn 0.15s ease;
}

.qr-modal {
  background: white;
  border-radius: 12px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  padding: 24px;
  max-width: 360px;
  width: 100%;
  animation: slideUp 0.2s ease;
  border: 1px solid #e5e7eb;
}

.qr-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  h4 {
    margin: 0;
    color: #111827;
    font-size: 16px;
    font-weight: 600;
  }
}

.qr-close-btn {
  background: white;
  border: 1px solid #d1d5db;
  font-size: 13px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.15s ease;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #374151;
  }

  &:active {
    background: #f3f4f6;
  }
}

.qr-code-container {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.qr-canvas {
  border-radius: 4px;
  display: block;
}

.qr-url {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
  word-break: break-all;
  text-align: center;
  background: #f9fafb;
  padding: 10px 12px;
  border-radius: 6px;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  line-height: 1.5;
  border: 1px solid #e5e7eb;
}
</style>
